import { ChatAvailabilityEnum } from 'lib-trend-core';
import { ChatStatusEntry } from '../state/chat.state';

/**
 * Presence/status presentation helpers (SPEC-2026-07-internal-chat, T-21 ·
 * RF-19/RF-29). Shared by the status button, the team view and the sidebar rows
 * so a colleague looks identical on every surface.
 *
 * Two rules encoded here:
 * - **Manual availability overrides derived presence** (RF-29/AC-25): BUSY and
 *   AWAY keep showing even while the user has a live session; only ACTIVE falls
 *   back to the socket-derived online/offline dot.
 * - **Expired statuses are ignored client-side** (AC-25): the backend clears
 *   them lazily on the next read, so the UI must not keep rendering a status
 *   past `statusExpiresAt`.
 */

/** Availability + presence resolved into what a row/badge renders. */
export interface ChatPresenceView {
  availability: ChatAvailabilityEnum;
  /** Derived socket presence (ignored for BUSY/AWAY). */
  online: boolean;
  /** Bootstrap contextual background class for the presence dot. */
  dotClass: string;
  /** Matching soft-badge classes for the availability pill. */
  badgeClass: string;
  /** i18n key of the availability label. */
  labelKey: string;
  /** Custom status emoji, expiry already applied. */
  emoji: string;
  /** Custom status text, expiry already applied. */
  text: string;
  /** Whether a non-expired custom status (emoji and/or text) is set. */
  hasCustomStatus: boolean;
}

/**
 * MatDialog config of the status picker. Shared so every surface that offers
 * "change my status" (the status button and the bubble popup's identity card)
 * opens the SAME dialog geometry — the two cannot drift apart.
 */
export const CHAT_STATUS_DIALOG_CONFIG = { width: '480px', maxWidth: '95vw' };

/** Availability options offered by the picker, in display order. */
export const CHAT_AVAILABILITY_OPTIONS: ReadonlyArray<{
  availability: ChatAvailabilityEnum;
  labelKey: string;
  dotClass: string;
  icon: string;
}> = [
  { availability: ChatAvailabilityEnum.ACTIVE, labelKey: 'chat.status.availabilityActive', dotClass: 'bg-success', icon: 'ri-checkbox-blank-circle-fill' },
  { availability: ChatAvailabilityEnum.BUSY, labelKey: 'chat.status.availabilityBusy', dotClass: 'bg-danger', icon: 'ri-indeterminate-circle-fill' },
  { availability: ChatAvailabilityEnum.AWAY, labelKey: 'chat.status.availabilityAway', dotClass: 'bg-warning', icon: 'ri-time-fill' },
];

/** Curated emoji grid of the custom-status picker (no free emoji keyboard). */
export const CHAT_STATUS_EMOJIS: ReadonlyArray<string> = [
  '💻', '🧠', '📞', '📅', '🎧', '☕',
  '🍽️', '🚗', '🏠', '🏖️', '🤒', '🔕',
];

/** Expiry presets — `minutes: 0` means "no expiry", `-1` means "end of today". */
export const CHAT_STATUS_EXPIRY_PRESETS: ReadonlyArray<{ key: string, labelKey: string, minutes: number }> = [
  { key: 'NONE', labelKey: 'chat.status.expiryNone', minutes: 0 },
  { key: 'M30', labelKey: 'chat.status.expiry30m', minutes: 30 },
  { key: 'H1', labelKey: 'chat.status.expiry1h', minutes: 60 },
  { key: 'H2', labelKey: 'chat.status.expiry2h', minutes: 120 },
  { key: 'H4', labelKey: 'chat.status.expiry4h', minutes: 240 },
  { key: 'TODAY', labelKey: 'chat.status.expiryToday', minutes: -1 },
];

/** Availability label key (also used for offline colleagues). */
export function availabilityLabelKey(availability: ChatAvailabilityEnum, online: boolean): string {
  if (availability === ChatAvailabilityEnum.BUSY) return 'chat.status.availabilityBusy';
  if (availability === ChatAvailabilityEnum.AWAY) return 'chat.status.availabilityAway';
  return online ? 'chat.status.availabilityActive' : 'chat.status.availabilityOffline';
}

/** True when a custom status carries an expiry that already elapsed. */
export function isStatusExpired(status: ChatStatusEntry | undefined, now: number = Date.now()): boolean {
  if (!status?.statusExpiresAt) return false;
  const expiresAt = new Date(status.statusExpiresAt).getTime();
  return !Number.isNaN(expiresAt) && expiresAt <= now;
}

/**
 * Resolves a user's presence dot + labels. `status` is the store entry (may be
 * absent), `online` the derived socket presence. Pass `now` to keep timed
 * recomputations deterministic.
 */
export function resolvePresenceView(
  status: ChatStatusEntry | undefined,
  online: boolean,
  now: number = Date.now(),
): ChatPresenceView {
  const expired = isStatusExpired(status, now);
  // An expired status reverts the whole entry (availability included) — that is
  // exactly what the backend does on its next lazy read.
  const availability = (expired ? ChatAvailabilityEnum.ACTIVE : status?.availability) ?? ChatAvailabilityEnum.ACTIVE;
  const emoji = expired ? '' : (status?.statusEmoji ?? '');
  const text = expired ? '' : (status?.statusText ?? '');

  let variant = online ? 'success' : 'secondary';
  if (availability === ChatAvailabilityEnum.BUSY) variant = 'danger';
  if (availability === ChatAvailabilityEnum.AWAY) variant = 'warning';

  return {
    availability,
    online,
    dotClass: `bg-${variant}`,
    badgeClass: `bg-soft-${variant} text-${variant}`,
    labelKey: availabilityLabelKey(availability, online),
    emoji,
    text,
    hasCustomStatus: !!emoji || !!text,
  };
}

/**
 * Converts an expiry preset into an ISO string for the STATUS_SET payload.
 * Returns `undefined` for "no expiry" and for a boundary that already passed
 * (e.g. "end of today" chosen at 23:59:59) — the backend refuses past dates.
 */
export function resolveExpiryIso(presetKey: string, now: Date = new Date()): string | undefined {
  const preset = CHAT_STATUS_EXPIRY_PRESETS.find((option) => option.key === presetKey);
  if (!preset || preset.minutes === 0) return undefined;

  const expiresAt = new Date(now.getTime());
  if (preset.minutes === -1) {
    expiresAt.setHours(23, 59, 59, 999);
  } else {
    expiresAt.setMinutes(expiresAt.getMinutes() + preset.minutes);
  }

  return expiresAt.getTime() > now.getTime() ? expiresAt.toISOString() : undefined;
}
